import { useState } from 'react';

import { type AssetBundleRow, type AssetBundleSortKey, compareAssetBundles } from './utils';

interface SortConfig {
	readonly key: AssetBundleSortKey;
	readonly direction: 'asc' | 'desc';
}

interface UseAssetBundleSortingResult {
	readonly sortedRows: ReadonlyArray<AssetBundleRow>;
	readonly sortIndicator: (key: AssetBundleSortKey) => string | null;
	readonly sortAriaLabel: (label: string, key: AssetBundleSortKey) => string;
	readonly toggleSort: (key: AssetBundleSortKey) => void;
}

export function useAssetBundleSorting(rows: ReadonlyArray<AssetBundleRow>): UseAssetBundleSortingResult {
	const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

	const sortedRows =
		sortConfig !== null
			? [...rows].sort((a, b) => {
					const result = compareAssetBundles(a, b, sortConfig.key);
					return sortConfig.direction === 'asc' ? result : -result;
				})
			: rows;

	const toggleSort = (key: AssetBundleSortKey) => {
		setSortConfig(prev => {
			if (prev?.key === key) {
				return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
			}

			return { key, direction: 'asc' };
		});
	};

	const sortIndicator = (key: AssetBundleSortKey): string | null => {
		if (sortConfig?.key !== key) {
			return null;
		}

		return sortConfig.direction === 'asc' ? ' ^' : ' v';
	};

	const sortAriaLabel = (label: string, key: AssetBundleSortKey): string => {
		if (sortConfig?.key !== key) {
			return `Sort by ${label}.`;
		}

		const currentDirection = sortConfig.direction === 'asc' ? 'ascending' : 'descending';
		const nextDirection = sortConfig.direction === 'asc' ? 'descending' : 'ascending';

		return `${label}, sorted ${currentDirection}. Activate to sort ${nextDirection}.`;
	};

	return {
		sortedRows,
		sortIndicator,
		sortAriaLabel,
		toggleSort
	};
}
